// ═══════════════════════════════════════════════════════════════════════
// CostCentreExpensePage — Sprint 2026-Q1d / Step ⑦ · Pass 1 · Report ⑤
// ═══════════════════════════════════════════════════════════════════════
// NOT a P&L report. Depreciation-only Cost-Centre view.
//
// STRICT BOUNDARY (per frozen architecture):
//   Pass 1 includes DEPRECIATION ONLY (snapshots + adjustments).
//   NO Purchase Invoices · NO Salary · NO Rent · NO Service Cost ·
//   NO Material — those flow in via Management Accounting Stage 3 and
//   the Journal Voucher module.
//
// Historical immutability is automatic via the snapshot's frozen
// cost_centre_id (Step ⑤ + Step ⑥).
// ═══════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/ui-helpers";
import { ViewOverlay } from "@/hooks/useViewOverlay";
import { useFormTab } from "@/hooks/useFormTab";
import {
  Filter as FilterIcon, RefreshCw, Download, Search, X,
  CheckCircle2, AlertTriangle, ArrowRight, FileText, Lock,
  Building2, Cpu,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import UserLabel, { useUserDirectory } from "@/components/UserLabel";

const currentFy = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth();
  const startYear = m >= 3 ? y : y - 1;
  return {
    from_period: `${startYear}-04`,
    to_period:   `${(startYear + 1).toString().padStart(4, "0")}-03`,
  };
};

const DRILL_SOURCES = [
  { key: "asset_register",            label: "Asset Register (live tagged to CC)" },
  { key: "depreciation_snapshots",    label: "Depreciation Snapshots" },
  { key: "depreciation_adjustments",  label: "Depreciation Adjustments" },
];

const PROJECT_GROUPS = ["Revenue Project", "Internal Project",
                         "Corporate Project", "Capital Project"];


export default function CostCentreExpensePage() {
  useUserDirectory();
  const openFormTab = useFormTab();
  const [{ from_period, to_period }, setRange] = useState(currentFy());
  const [filters, setFilters] = useState({
    branch_id: "", business_vertical_id: "",
    project_group_name: "", cost_centre_id: "",
    asset_category: "",
    include_inactive: false,
    include_unassigned: true,
  });
  const [branches, setBranches] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [costCentres, setCostCentres] = useState([]);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [reconcile, setReconcile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/branches", { params: { limit: 500 } })
      .then(r => setBranches(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setBranches([]));
    api.get("/work-types", { params: { limit: 500 } })
      .then(r => setVerticals(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setVerticals([]));
    api.get("/mca/cost-centres", { params: { limit: 500 } })
      .then(r => setCostCentres(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setCostCentres([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        from_period, to_period,
        include_inactive: filters.include_inactive,
        include_unassigned: filters.include_unassigned,
      };
      ["branch_id", "business_vertical_id", "project_group_name",
        "cost_centre_id", "asset_category"].forEach(k => {
          if (filters[k]) params[k] = filters[k];
      });
      const recParams = { from_period, to_period };
      ["branch_id", "business_vertical_id", "project_group_name",
        "asset_category"].forEach(k => {
          if (filters[k]) recParams[k] = filters[k];
      });
      const [res, rec] = await Promise.all([
        api.get("/reports/cost-centre-expense/_summary", { params }),
        api.get("/reports/cost-centre-expense/_reconcile", { params: recParams }),
      ]);
      setRows(res.data?.rows || []);
      setTotals(res.data?.totals || {});
      setReconcile(rec.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Cost-Centre Expense Report.");
    } finally {
      setLoading(false);
    }
  }, [from_period, to_period, filters]);

  useEffect(() => { load(); }, [load]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      (r.cost_centre_code || "").toLowerCase().includes(q) ||
      (r.cost_centre_name || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const openDrill = (row, source) => {
    const meta = DRILL_SOURCES.find(s => s.key === source);
    const ccid = row.cost_centre_id || "_unassigned";
    openFormTab({
      path: `/reports/cost-centre-expense/drill/${ccid}/${source}`,
      label: `${meta?.label || source} · ${row.cost_centre_name}`,
      parent: "/reports/cost-centre-expense",
      render: ({ close }) => (
        <CostCentreDrillTab
          row={row}
          source={source}
          from_period={from_period}
          to_period={to_period}
          extraFilters={filters}
          onClose={close}
        />
      ),
    });
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams({
        from_period, to_period,
        include_inactive: String(filters.include_inactive),
        include_unassigned: String(filters.include_unassigned),
      });
      ["branch_id", "business_vertical_id", "project_group_name",
        "cost_centre_id", "asset_category"].forEach(k => {
          if (filters[k]) params.append(k, filters[k]);
      });
      const resp = await api.get(`/reports/cost-centre-expense/export.csv?${params}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cost-centre-expense-${from_period}_${to_period}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch {
      toast.error("Failed to export CSV.");
    }
  };

  const reconcilePass = reconcile?.checks?.every(c => c.status === "PASS");

  return (
    <div className="space-y-6 p-6" data-testid="cost-centre-expense-page">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Step ⑦ · Pass 1 · Report ⑤ · Depreciation Only"
        title="Cost-Centre Expense Report"
        subtitle="Depreciation expense consumed by each Cost Centre. Operational view — Purchase Invoices, Salary, Rent and other operating expenses arrive in Management Accounting Stage 3."
        actions={
          <div className="flex items-center gap-2">
            <button
              data-testid="cost-centre-expense-refresh-btn"
              onClick={load}
              disabled={loading}
              className="text-xs uppercase tracking-wider font-bold px-3 py-2 border border-slate-300 hover:bg-slate-50">
              <RefreshCw size={12} className={`inline mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              data-testid="cost-centre-expense-export-csv-btn"
              onClick={exportCsv}
              className="text-xs uppercase tracking-wider font-bold px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white">
              <Download size={12} className="inline mr-1" /> Export CSV
            </button>
          </div>
        }
      />

      {/* Boundary banner */}
      <div className="border-l-4 border-indigo-500 bg-indigo-50/40 p-3" data-testid="cost-centre-expense-scope-banner">
        <div className="flex items-start gap-2">
          <Lock size={14} className="text-indigo-700 flex-none mt-0.5" />
          <div className="text-xs text-indigo-900">
            <span className="font-bold">Pass 1 scope — Depreciation only.</span>{" "}
            Purchase Invoices, Salary, Rent, Service Cost and Material Cost are intentionally excluded — those classes of expense flow in via Management Accounting Stage 3 and the future Journal Voucher module. Historical attribution is immutable: snapshots freeze the cost centre at posting time.
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <FilterIcon size={14} className="text-slate-500" />
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Filters</div>
        </div>
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
          <FilterField label="Period From">
            <input
              data-testid="cost-centre-expense-filter-from"
              type="month"
              value={from_period}
              onChange={e => setRange(r => ({ ...r, from_period: e.target.value }))}
              className={inputCls}
            />
          </FilterField>
          <FilterField label="Period To">
            <input
              data-testid="cost-centre-expense-filter-to"
              type="month"
              value={to_period}
              onChange={e => setRange(r => ({ ...r, to_period: e.target.value }))}
              className={inputCls}
            />
          </FilterField>
          <FilterField label="Branch">
            <select
              data-testid="cost-centre-expense-filter-branch"
              value={filters.branch_id}
              onChange={e => setFilters(f => ({ ...f, branch_id: e.target.value }))}
              className={inputCls}>
              <option value="">All Branches</option>
              {branches.map(b =>
                <option key={b.id} value={b.id}>{b.branch_code} — {b.branch_name}</option>
              )}
            </select>
          </FilterField>
          <FilterField label="Business Vertical">
            <select
              data-testid="cost-centre-expense-filter-vertical"
              value={filters.business_vertical_id}
              onChange={e => setFilters(f => ({ ...f, business_vertical_id: e.target.value }))}
              className={inputCls}>
              <option value="">All Verticals</option>
              {verticals.map(v =>
                <option key={v.id} value={v.id}>{v.name}</option>
              )}
            </select>
          </FilterField>
          <FilterField label="Project Group">
            <select
              data-testid="cost-centre-expense-filter-project-group"
              value={filters.project_group_name}
              onChange={e => setFilters(f => ({ ...f, project_group_name: e.target.value }))}
              className={inputCls}>
              <option value="">All Groups</option>
              {PROJECT_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </FilterField>
          <FilterField label="Cost Centre">
            <select
              data-testid="cost-centre-expense-filter-cost-centre"
              value={filters.cost_centre_id}
              onChange={e => setFilters(f => ({ ...f, cost_centre_id: e.target.value }))}
              className={inputCls}>
              <option value="">All Cost Centres</option>
              {costCentres.map(c =>
                <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
              )}
              <option value="_unassigned">(Unassigned only)</option>
            </select>
          </FilterField>
          <FilterField label="Asset Category">
            <input
              data-testid="cost-centre-expense-filter-asset-category"
              type="text"
              placeholder="e.g. IT, MEP, Vehicle…"
              value={filters.asset_category}
              onChange={e => setFilters(f => ({ ...f, asset_category: e.target.value }))}
              className={inputCls}
            />
          </FilterField>
          <FilterField label="Visibility">
            <div className="space-y-1">
              <label className="flex items-center gap-2 text-[11px]">
                <input
                  data-testid="cost-centre-expense-filter-include-inactive"
                  type="checkbox"
                  checked={filters.include_inactive}
                  onChange={e => setFilters(f => ({ ...f, include_inactive: e.target.checked }))}
                  className="h-3.5 w-3.5"
                />
                Inactive CCs
              </label>
              <label className="flex items-center gap-2 text-[11px]">
                <input
                  data-testid="cost-centre-expense-filter-include-unassigned"
                  type="checkbox"
                  checked={filters.include_unassigned}
                  onChange={e => setFilters(f => ({ ...f, include_unassigned: e.target.checked }))}
                  className="h-3.5 w-3.5"
                />
                Unassigned bucket
              </label>
            </div>
          </FilterField>
        </div>
      </Card>

      {/* KPI tiles */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile icon={<Building2 size={14} />} label="Total Cost Centres" value={totals.cost_centre_count} tone="slate" suffix="" prefix="" raw />
        <KpiTile icon={<Cpu size={14} />}       label="Total Assets"        value={totals.asset_count}       tone="indigo" suffix="" prefix="" raw />
        <KpiTile icon={<ArrowRight size={14} />} label="Total Monthly Depreciation" value={totals.monthly_depreciation} tone="amber" prefix="-" />
        <KpiTile icon={<Building2 size={14} />} label="Total Closing Net Block" value={totals.closing_nbv} tone="emerald-bold" />
      </div>

      {/* Reconciliation badge */}
      {reconcile && (
        <Card className={`p-4 border ${reconcilePass ? "border-emerald-300 bg-emerald-50/40" : "border-amber-300 bg-amber-50/40"}`}>
          <div className="flex items-start gap-3" data-testid="cost-centre-expense-reconcile-banner">
            {reconcilePass
              ? <CheckCircle2 size={18} className="text-emerald-700 flex-none mt-0.5" />
              : <AlertTriangle size={18} className="text-amber-700 flex-none mt-0.5" />}
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-700">
                Reconciliation — CC roll-up vs Source
              </div>
              <div className="font-display text-base font-black text-slate-900 mt-0.5">
                {reconcilePass
                  ? "All checks PASS — every depreciation row is attributed to exactly one Cost Centre."
                  : `${reconcile.checks.filter(c => c.status === "FAIL").length} of ${reconcile.checks.length} check(s) failing.`}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mt-2">
                {reconcile.checks.map((c, i) => (
                  <div key={i}
                       data-testid={`cost-centre-expense-reconcile-${c.check.toLowerCase().replace(/[^a-z]/g, "-")}`}
                       className={`text-xs px-2 py-1 rounded ${c.status === "PASS"
                                    ? "bg-emerald-100 text-emerald-900"
                                    : "bg-amber-100 text-amber-900"}`}>
                    <div className="font-bold">{c.status} · {c.check}</div>
                    {c.status === "FAIL" && (
                      <div className="text-[10px] mt-0.5">
                        Δ ₹{c.delta.toLocaleString("en-IN")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Search */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="cost-centre-expense-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search cost centre code / name…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 focus:border-[#F97316] focus:outline-none"
          />
        </div>
        <div className="text-xs text-slate-500">
          {filteredRows.length} of {rows.length} cost centres
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <Card className="p-12 text-center text-slate-500">Loading Cost-Centre Expense Report…</Card>
      ) : rows.length === 0 ? (
        <EmptyState title="No cost centres in this view"
                    hint="Try toggling 'Include inactive' or widen the period." />
      ) : filteredRows.length === 0 ? (
        <EmptyState title="No matches" hint="Clear the search to see all cost centres." />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cost-centre-expense-table">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>
                  {["Cost Centre", "# Assets", "Opening Net Block",
                    "Monthly Depreciation", "Closing Net Block"].map(h =>
                      <th key={h}
                          className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-3">
                        {h}
                      </th>
                    )}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(r => {
                  const isUnassigned = !r.cost_centre_id;
                  const ccid = r.cost_centre_id || "_unassigned";
                  return (
                    <tr key={ccid}
                        data-testid={`cost-centre-expense-row-${ccid}`}
                        className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3">
                        <div className={`font-mono font-bold ${isUnassigned ? "text-slate-500 italic" : "text-[#F97316]"}`}>
                          {r.cost_centre_code || (isUnassigned ? "—" : "—")}
                        </div>
                        <div className={`text-xs ${isUnassigned ? "text-slate-500 italic" : "text-slate-700"}`}>
                          {r.cost_centre_name}
                          {isUnassigned && (
                            <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">orphan</span>
                          )}
                        </div>
                      </td>
                      <DrillCell value={r.asset_count} raw onClick={() => r._counts.assets && openDrill(r, "asset_register")} count={r._counts.assets} tone="indigo" />
                      <td className="py-2 px-3 text-right tabular-nums text-xs text-slate-700">
                        {r.opening_nbv ? formatINR(r.opening_nbv) : "—"}
                      </td>
                      <DrillCell value={r.monthly_depreciation} prefix="-" onClick={() => r._counts.snapshots && openDrill(r, "depreciation_snapshots")} count={r._counts.snapshots + r._counts.adjustments} tone="amber" />
                      <td className="py-2 px-3 text-right tabular-nums text-sm font-black text-emerald-800">
                        {r.closing_nbv ? formatINR(r.closing_nbv) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-100 border-t-2 border-slate-300 font-black">
                <tr data-testid="cost-centre-expense-totals-row">
                  <td className="py-3 px-3 text-xs uppercase tracking-wider">Totals ({totals.cost_centre_count})</td>
                  <td className="py-3 px-3 text-right tabular-nums">{totals.asset_count || 0}</td>
                  <td className="py-3 px-3 text-right tabular-nums">{formatINR(totals.opening_nbv || 0)}</td>
                  <td className="py-3 px-3 text-right tabular-nums text-amber-800">-{formatINR(totals.monthly_depreciation || 0)}</td>
                  <td className="py-3 px-3 text-right tabular-nums text-emerald-800">{formatINR(totals.closing_nbv || 0)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}

      {/* Data Source footer */}
      <Card className="p-4 bg-slate-50">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-600 font-bold mb-2">
          <FileText size={11} className="inline mr-1" /> Data Sources (Pass 1)
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-1 text-xs text-slate-700">
          <div>• Depreciation Snapshots <span className="text-slate-400">(frozen at Step ⑤)</span></div>
          <div>• Depreciation Adjustments <span className="text-slate-400">(append-only Step ⑥)</span></div>
          <div>• Asset Register <span className="text-slate-400">(headcount per CC)</span></div>
          <div className="text-slate-400">• Purchase Invoices <span className="italic">(MA Stage 3)</span></div>
          <div className="text-slate-400">• Salary / Rent / Service Cost <span className="italic">(JV Module)</span></div>
        </div>
      </Card>
    </div>
  );
}


// ─── Drill-down tab ─────────────────────────────────────────────────────────

function CostCentreDrillTab({ row, source, from_period, to_period, extraFilters, onClose }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const meta = DRILL_SOURCES.find(s => s.key === source) || { label: source };
  const ccid = row.cost_centre_id || "_unassigned";

  useEffect(() => {
    setLoading(true);
    const params = { from_period, to_period, limit: 500 };
    ["branch_id", "business_vertical_id", "project_group_name",
      "asset_category"].forEach(k => {
        if (extraFilters[k]) params[k] = extraFilters[k];
    });
    api.get(`/reports/cost-centre-expense/${ccid}/drill/${source}`, { params })
      .then(r => setData(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load drill-down."))
      .finally(() => setLoading(false));
  }, [ccid, source, from_period, to_period, extraFilters]);

  return (
    <ViewOverlay onClose={onClose} testid="cost-centre-expense-drill-overlay">
      <Toaster richColors position="top-right" />
      <div className="p-6 space-y-4">
        <PageHeader
          eyebrow={`Drill-Down · ${row.cost_centre_code || "Unassigned"}`}
          title={meta.label}
          subtitle={`${row.cost_centre_name} · ${from_period} → ${to_period}`}
          actions={
            <button onClick={onClose}
                    className="text-xs uppercase tracking-wider text-slate-500 hover:text-slate-900">
              <X size={16} className="inline mr-1" /> Close
            </button>
          }
        />
        {loading
          ? <div className="text-center text-slate-500 py-12">Loading source documents…</div>
          : data.length === 0
          ? <EmptyState title="No source documents in this scope" hint="Adjust filters or widen the period." />
          : <Card className="overflow-x-auto">
              <DrillTable source={source} rows={data} />
            </Card>}
      </div>
    </ViewOverlay>
  );
}


function DrillTable({ source, rows }) {
  if (source === "asset_register") {
    return (
      <table className="w-full text-sm" data-testid="drill-table-assets">
        <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider font-bold text-slate-500">
          <tr><th className="text-left p-3">Asset</th><th className="text-left p-3">Category</th><th className="text-left p-3">Branch</th><th className="text-right p-3">Purchase Value</th><th className="text-left p-3">Status</th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-slate-100">
              <td className="p-3">
                <span className="font-mono font-bold text-[#F97316]">{r.asset_code}</span>
                <span className="text-slate-400 ml-1 text-xs">· {r.name}</span>
              </td>
              <td className="p-3 text-xs">{r.category || "—"}</td>
              <td className="p-3 text-xs">{r.branch_name || "—"}</td>
              <td className="p-3 text-right tabular-nums font-bold">{formatINR(r.purchase_value || 0)}</td>
              <td className="p-3 text-xs">
                <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-bold ${
                  r.status === "Scrapped" ? "bg-rose-100 text-rose-800" :
                  r.status === "Assigned" ? "bg-amber-100 text-amber-800" :
                                            "bg-emerald-100 text-emerald-800"
                }`}>{r.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (source === "depreciation_snapshots") {
    return (
      <table className="w-full text-sm" data-testid="drill-table-snap">
        <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider font-bold text-slate-500">
          <tr><th className="text-left p-3">Period</th><th className="text-left p-3">Asset</th><th className="text-right p-3">Opening NBV</th><th className="text-right p-3">Depreciation</th><th className="text-right p-3">Closing NBV</th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-slate-100">
              <td className="p-3 font-mono font-bold">{r.period_key}</td>
              <td className="p-3 text-xs">
                <span className="font-mono font-bold text-[#F97316]">{r.asset_code}</span>
                <span className="text-slate-400 ml-1">· {r.asset_name}</span>
              </td>
              <td className="p-3 text-right tabular-nums">{formatINR(r.opening_nbv || 0)}</td>
              <td className="p-3 text-right tabular-nums font-bold text-amber-800">-{formatINR(r.monthly_depreciation || 0)}</td>
              <td className="p-3 text-right tabular-nums">{formatINR(r.closing_nbv || 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (source === "depreciation_adjustments") {
    return (
      <table className="w-full text-sm" data-testid="drill-table-adj">
        <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider font-bold text-slate-500">
          <tr><th className="text-left p-3">Period</th><th className="text-left p-3">Asset</th><th className="text-right p-3">Δ</th><th className="text-left p-3">Reason</th><th className="text-left p-3">By</th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-slate-100">
              <td className="p-3 font-mono font-bold">{r.period_key}</td>
              <td className="p-3 text-xs">{r.asset_code || "—"}</td>
              <td className={`p-3 text-right tabular-nums font-bold ${r.delta >= 0 ? "text-rose-700" : "text-emerald-700"}`}>
                {r.delta >= 0 ? "+" : ""}{formatINR(r.delta)}
              </td>
              <td className="p-3 text-xs max-w-md truncate" title={r.reason}>{r.reason}</td>
              <td className="p-3 text-[10px] text-slate-500"><UserLabel value={r.posted_by} fallback="—" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return null;
}


// ─── Small helpers ──────────────────────────────────────────────────────────

const inputCls = "w-full border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#F97316]";

function FilterField({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function KpiTile({ icon, label, value, tone = "slate", suffix = "", prefix = "", raw = false }) {
  const palettes = {
    "slate":         "bg-slate-50 border-slate-200 text-slate-900",
    "indigo":        "bg-indigo-50 border-indigo-200 text-indigo-900",
    "amber":         "bg-amber-50 border-amber-200 text-amber-900",
    "emerald-bold":  "bg-emerald-50 border-emerald-200 text-emerald-900",
  };
  const v = (value === null || value === undefined)
    ? "—"
    : raw
    ? `${prefix}${value}${suffix}`
    : (value === 0)
    ? "—"
    : `${prefix}₹${Number(value).toLocaleString("en-IN")}${suffix}`;
  return (
    <div className={`p-4 border ${palettes[tone]}`}
         data-testid={`cost-centre-expense-kpi-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-[0.15em] font-bold opacity-70">
        {icon} <span className="ml-1">{label}</span>
      </div>
      <div className="text-xl font-black tabular-nums mt-1">{v}</div>
    </div>
  );
}

function DrillCell({ value, onClick, count, tone = "slate", prefix = "", raw = false }) {
  const disabled = !count || count === 0;
  const palette = {
    "indigo":  "text-indigo-700",
    "amber":   "text-amber-800",
    "slate":   "text-slate-700",
  }[tone] || "text-slate-700";
  return (
    <td className={`py-2 px-3 text-right tabular-nums text-xs ${palette} ${disabled ? "" : "cursor-pointer hover:underline"}`}
        onClick={disabled ? undefined : onClick}
        data-testid={disabled ? undefined : `drill-cell-${tone}`}
        title={disabled ? "No documents in this scope" : `Click to see ${count} document(s)`}>
      {value
        ? <>{prefix}{raw ? value : formatINR(value)}{!disabled && <ArrowRight size={9} className="inline ml-1 opacity-40" />}</>
        : <span className="text-slate-300">—</span>}
    </td>
  );
}
